import { useRef } from 'react';
import { ArrowDropDownRounded } from '@mui/icons-material';
import { defaultCountries, parseCountry, FlagImage } from 'react-international-phone';

import Popper from '@components/Popper';
import Text from '@components/Text';
import { getCountryName, getLocale, getLocaleInfo, t, type LocaleInfo } from '@i18n';

import './styles.scss';

export function countryDisplayName(iso2, fallback = '', locale = getLocale()) {
    const code = String(iso2 || '').toUpperCase();

    if (!code) {
        return fallback;
    }

    return getCountryName(code, locale, fallback);
}

export default function CountrySelector({ value, onChange, locale }: { value: string; usdAvailable?: boolean; onChange?: (country: string) => void; locale?: LocaleInfo }) {
    const popperRef = useRef(null);
    const buttonRef = useRef(null);

    const iso2 = String(value || 'BR').toLowerCase();
    const localeInfo = locale || getLocaleInfo(iso2);

    function handleOpen() {
        if (!popperRef.current || !buttonRef.current) return;

        const options = defaultCountries.map((rawCountry) => {
            const { name, iso2: optionIso2 } = parseCountry(rawCountry);

            return {
                id: optionIso2,
                label: countryDisplayName(optionIso2, name, localeInfo.code),
                icon: <FlagImage iso2={optionIso2} size="18px" style={{ borderRadius: 2, flexShrink: 0 }} />,
                onClick: () => {
                    if (optionIso2 !== iso2) {
                        onChange?.(optionIso2.toUpperCase());
                    }
                },
            };
        });

        popperRef.current.open(options, 'bottom-end', buttonRef.current, {
            searchable: true,
            searchPlaceholder: t('input.country.searchPlaceholder'),
        });
    }

    return (
        <Popper controlRef={popperRef} className="country-selector-popper">
            <button
                type="button"
                ref={buttonRef}
                className="country-selector"
                data-testid="country-selector"
                onClick={handleOpen}
            >
                <FlagImage iso2={iso2} size="18px" style={{ borderRadius: 2, flexShrink: 0 }} />
                <Text.small className="country-selector-name">{localeInfo.countryName}</Text.small>
                <ArrowDropDownRounded sx={{ fontSize: 18 }} />
            </button>
        </Popper>
    );
}
